import { Package, X } from 'lucide-react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { Button } from '@nucleus-commerce/ui/components/button';
import { Skeleton } from '@nucleus-commerce/ui/components/skeleton';

import { ProductPicker, StoreVariantPicker } from '@/features/resource-pickers';

import { usePostProducts } from '../hooks/use-post-products';
import { MAX_POST_PRODUCTS, type PostVariantForm } from '../schemas/post-form-schema';

export interface PostProductsFieldProps {
  storeId: string;
  productIds: string[];
  variants: PostVariantForm[];
  onChange: (next: { productIds: string[]; variants: PostVariantForm[] }) => void;
  disabled?: boolean;
}

/**
 * The products a post is about, picked with the store's product picker (product or variant
 * mode). The first product is what "Fill from product" and the backend's prefill read.
 */
export function PostProductsField({
  storeId,
  productIds,
  variants,
  onChange,
  disabled = false,
}: PostProductsFieldProps) {
  const { t } = useTranslation('social');
  const [picker, setPicker] = useState<'products' | 'variants' | null>(null);
  const rows = usePostProducts(productIds);

  const removeProduct = (productId: string) =>
    onChange({
      productIds: productIds.filter((id) => id !== productId),
      variants: variants.filter((v) => v.productId !== productId),
    });
  const removeVariant = (variantId: string) =>
    onChange({ productIds, variants: variants.filter((v) => v.variantId !== variantId) });

  return (
    <div className="flex flex-col gap-3">
      {productIds.length === 0 ? (
        <p className="text-muted-foreground rounded-md border border-dashed p-4 text-center text-sm">
          {t('editor.products.empty')}
        </p>
      ) : (
        <ul className="divide-y rounded-md border" aria-label={t('editor.products.label')}>
          {rows.map((row, index) => {
            const own = variants.filter((v) => v.productId === row.productId);
            return (
              <li key={row.productId} className="flex items-center gap-3 px-3 py-2">
                <Package className="text-muted-foreground size-4 shrink-0" aria-hidden />
                <div className="min-w-0 flex-1">
                  {row.isPending ? (
                    <Skeleton className="h-4 w-40" />
                  ) : (
                    <span className="block truncate text-sm font-medium">
                      {row.product?.title ?? t('editor.products.unknown')}
                    </span>
                  )}
                  <span className="text-muted-foreground block truncate text-xs">
                    {index === 0 ? `${t('editor.products.primary')} · ` : ''}
                    {own.length > 0
                      ? t('editor.products.variantCount', { count: own.length })
                      : t('editor.products.wholeProduct')}
                  </span>
                  {own.length > 0 && !disabled ? (
                    <ul className="mt-1 flex flex-wrap gap-1">
                      {own.map((v) => (
                        <li key={v.variantId}>
                          <Button
                            type="button"
                            variant="outline"
                            size="xs"
                            aria-label={t('editor.products.removeVariant', {
                              title: v.title ?? t('editor.products.variant'),
                            })}
                            onClick={() => removeVariant(v.variantId)}
                          >
                            {v.title ?? t('editor.products.variant')}
                            <X aria-hidden />
                          </Button>
                        </li>
                      ))}
                    </ul>
                  ) : null}
                </div>
                {!disabled ? (
                  <Button
                    type="button"
                    variant="ghost"
                    size="icon-sm"
                    aria-label={t('editor.products.remove', {
                      title: row.product?.title ?? row.productId,
                    })}
                    onClick={() => removeProduct(row.productId)}
                  >
                    <X aria-hidden />
                  </Button>
                ) : null}
              </li>
            );
          })}
        </ul>
      )}
      {!disabled ? (
        <div className="flex flex-wrap gap-2">
          <Button type="button" variant="outline" size="sm" onClick={() => setPicker('products')}>
            {t('editor.products.pick')}
          </Button>
          <Button type="button" variant="outline" size="sm" onClick={() => setPicker('variants')}>
            {t('editor.products.pickVariants')}
          </Button>
        </div>
      ) : null}
      <ProductPicker
        open={picker === 'products'}
        onOpenChange={(open) => setPicker(open ? 'products' : null)}
        scope={{ kind: 'store', storeId }}
        title={t('editor.products.pick')}
        multiple={MAX_POST_PRODUCTS}
        initialSelectedIds={productIds}
        onConfirm={(selection) => {
          const next = new Set(selection.ids);
          onChange({
            productIds: selection.ids,
            variants: variants.filter((v) => next.has(v.productId)),
          });
        }}
      />
      <StoreVariantPicker
        open={picker === 'variants'}
        onOpenChange={(open) => setPicker(open ? 'variants' : null)}
        storeId={storeId}
        title={t('editor.products.pickVariants')}
        multiple={MAX_POST_PRODUCTS}
        initialSelectedVariantIds={variants.map((v) => v.variantId)}
        onConfirm={(selection) => {
          const kept = new Map(variants.map((v) => [v.variantId, v]));
          const picked: PostVariantForm[] = selection.childIds.map((variantId) => {
            const loaded = selection.children.find((c) => c.id === variantId);
            return (
              (loaded && {
                variantId,
                productId: loaded.item.productId,
                title: loaded.child.title,
              }) ??
              kept.get(variantId) ?? { variantId, productId: '', title: null }
            );
          });
          const ids = [...productIds];
          for (const v of picked)
            if (v.productId && !ids.includes(v.productId)) ids.push(v.productId);
          onChange({ productIds: ids, variants: picked.filter((v) => v.productId) });
        }}
      />
    </div>
  );
}
